import {
  MapInfo,
  ScenarioMode,
  ScenarioModelData,
} from "@citychallenge/common";
import { SceneModel } from "../scene/scene.model";
import { GameBaseModel } from "../game-session/game-session.model";

import { observable, action, computed } from "mobx";

export class ScenarioModel implements ScenarioModelData<SceneModel> {
  public game?: GameBaseModel;

  constructor(data: ScenarioModelData, game?: GameBaseModel) {
    this.id = data.id;
    this.game = game;
    this.scene = data.scene;
    this.mode = data.mode || "default";
    this.jumpTarget = data.jumpTarget;
    this._scenes = data.scenes.map(
      (sceneData) => new SceneModel(sceneData.scene, this)
    );
    if (data.originalScenes) {
      this._originalScenes = data.originalScenes.map(
        (sceneData) => new SceneModel(sceneData.scene, this)
      );
    }
    this.finished = !!data.finished;
    this.started = !!data.started;
    if (data.activeSceneId) {
      this.activeScene = this.findSceneById(data.activeSceneId);
    } else if (!this.finished) {
      this.activeScene = this.scenes[0];
    }
  }

  /* ID of the scenario */
  public readonly id: string;

  public readonly mode: ScenarioMode;

  public readonly jumpTarget?: string;

  // The parent scene of the scenario
  public readonly scene?: {
    id: string;
  };

  /** Pristine base-scenario scenes — set once at first launch, persisted across sessions */
  public _originalScenes?: SceneModel[];

  /** Capture pristine scenes if not already set (first launch only) */
  public captureOriginalScenes() {
    if (!this._originalScenes) {
      this._originalScenes = [...this._scenes];
    }
  }

  /* All scenes that the scenario can play in order */
  @observable
  private _scenes: SceneModel[];

  @computed
  public get scenes(): SceneModel[] {
    const scenes = this._scenes.filter((scene) => !scene.hidden);

    // Find the index of the first redirect scene if there is any
    const completeIndex = scenes.findIndex((scene) => scene.completesScenario);

    if (completeIndex >= 0) {
      // When a scene has entered the scenario that completed the scenario,
      // remove all scenes that follow it.
      return scenes.slice(0, completeIndex + 1);
    }

    return scenes;
  }

  @computed
  public get lastScene(): SceneModel | undefined {
    if (!this.scenes.length) return undefined;
    return this.scenes[this.scenes.length - 1];
  }

  /**
   * Helper function to prevent call stack loop in 'completesScenario' getter
   */
  public _safeIsLastScene(scene: SceneModel) {
    const scenes = this._scenes.filter((scene) => !scene.hidden);

    return !!scenes.length && scenes[scenes.length - 1] === scene;
  }

  /* The ID of the currently activated scene */
  @computed
  public get activeSceneId() {
    return this.activeScene && this.activeScene.id;
  }

  @computed
  public get activeSceneIndex() {
    if (!this.activeScene) return -1;

    const currentIndex = this.scenes.findIndex(
      (s) => s.id === this.activeScene!.id
    );
    return currentIndex;
  }

  @computed
  public get activeSceneIndexSlider() {
    const hasCompletionScene =
      !this.lastScene?.completesScenario && this.finished;

    return this.activeSceneIndex + (hasCompletionScene ? 1 : 0);
  }

  /**
   * This value is set by any UI component that controls the visibility of scenes.
   *
   * It is used by UI components not inside the element slider tree to determine
   * wether or not to show things like 'skip active scene'
   */
  @observable
  private visibleIndex = -1;

  @action
  public setVisibleIndex = (index: number) => {
    this.visibleIndex = index;
  };

  /**
   * Returns true if any slider is showing the latest scene in the scenario
   */
  public get activeSceneIsVisible() {
    return this.activeSceneIndex == this.visibleIndex;
  }

  /** The ID of the scene currently visible in the scroller (may differ from activeSceneId when scrolled back). */
  @computed
  public get visibleSceneId(): string | undefined {
    if (this.visibleIndex < 0) return undefined;
    return this.scenes[this.visibleIndex]?.id;
  }

  @computed
  public get numScenes(): number {
    return this.scenes.reduce((prev, scene) => {
      return prev + scene.sceneLength;
    }, 0);
  }

  /* The scene that is currently active */
  @observable
  public activeScene?: SceneModel = undefined;

  @action
  private _setActiveScene(scene: SceneModel) {
    this.activeScene = scene;
  }

  @action
  public __dangerouslySetActiveScene(scene: SceneModel) {
    this.activeScene = scene;
    this.finished = false;
  }

  @action public __dangerouslyAdvanceUntilActiveScene() {
    // Set all scenes before the active scene to completed
    for (const s of this.scenes) {
      if (s.id === this.activeSceneId) break;
      s.lifecycle.onStart();
    }

    this.activeScene?.lifecycle.onStart();
  }

  public findSceneById(id: string) {
    return this.scenes.find((scene) => scene.id === id);
  }

  public findNearestSceneStartingWith(id: string) {
    const rev = [...this.scenes].reverse();

    return rev.find(
      (scene) => scene.id === id || scene.id.startsWith(`${id}_`)
    );
  }

  /* True if the scenario has cycled trough all it's scenes */
  @observable
  public finished = false;

  @action
  private _setFinished(finished: boolean) {
    this.finished = finished;
  }

  public setFinished(finished: boolean) {
    return (this.finished = finished);
  }

  @observable
  public started = false;

  @action
  public setStarted(started: boolean) {
    this.started = started;
  }

  /**
   * Call this to forcefully finish the scenario
   */
  public forceFinishScenario() {
    this._setFinished(true);
  }

  @computed
  public get progress() {
    if (this.activeScene?.completesScenario) return 100;

    if (this.finished) return 100;
    if (!this.scenes.length) return 100;

    return (
      (this.scenes.filter((s) => s.isCompleted).length / this.scenes.length) *
      100
    );
  }

  @computed
  public get totalPoints() {
    return this.scenes.reduce<number>((prev, scene) => {
      return prev + scene.totalPoints;
    }, 0);
  }

  @computed
  public get isRunningInHotlink() {
    // If scenario isn't governed by a game we are running in hotlink for sure
    return !this.game;
  }

  @computed
  public get activeSceneSkippable() {
    return this.activeScene && !this.activeScene.isAnswered;
  }

  public skipActiveScene = () => {
    this.activeScene?.skip();
  };

  /* Callback that gets called when one of the scenes is finished with it's interaction */
  public readonly onSceneFinish = (scene: SceneModel) => {
    const currentIndex = this.scenes.findIndex((s) => s.id === scene.id);

    const nextIndex = currentIndex + 1;

    this.setActiveIndex(nextIndex);
  };

  public setActiveIndex = (index: number) => {
    if (index === this.scenes.length) {
      this._setFinished(true);
    } else {
      const activateScene = this.scenes[index]!;
      this._setActiveScene(activateScene);
    }
  };

  public get maximizeAccessibility() {
    return this.game?.maximizeAccessibility || false;
  }

  /* Deserialization */
  public toJSON(): ScenarioModelData {
    return {
      id: this.id,
      activeSceneId: this.activeSceneId,
      finished: this.finished,
      started: this.started,
      mode: this.mode,
      scene: this.scene,
      scenes: this._scenes.map((s, index) => {
        return {
          weight: index,
          scene: s.toJSON(),
        };
      }),
      ...(this._originalScenes && {
        originalScenes: this._originalScenes.map((s, index) => ({
          weight: index,
          scene: s.toJSON(),
        })),
      }),
    };
  }

  @action
  public filterScenes(testFn: (scene: SceneModel) => boolean) {
    this._scenes = this._scenes.filter(testFn);
  }

  public resetScenarioTo(sceneId: string) {
    const index = this._scenes.findIndex((scene) => scene.id === sceneId);

    if (index < 0) return;

    const copy = [...this._scenes];
    const removed = copy.splice(index + 1, this._scenes.length - index);

    copy.push(...removed.map((scene) => SceneModel.resetModel(scene, this)));

    this._scenes = copy;

    this._setFinished(false);

    // Find index in filtered scenes (excludes hidden scenes like logic branches)
    const filteredIndex = this.scenes.findIndex(
      (scene) => scene.id === sceneId
    );
    if (filteredIndex >= 0) {
      this.setActiveIndex(filteredIndex);
    }
  }

  @action
  public insertScenes(scenes: SceneModel[], skip = 0, sceneId?: string) {
    for (const scene of scenes) {
      scene.scenario = this;
    }

    // Create a copy of the current scenes
    const copy = [...this._scenes];

    const insertAtIndex =
      this._scenes.findIndex(
        (scene) => scene.id === (sceneId || this.activeSceneId)
      ) || 0;

    // Insert new scenes after the active index
    copy.splice(insertAtIndex + 1 + skip, 0, ...scenes);

    this._scenes = copy;
  }

  @computed
  public get sceneWithIncompleteDownloads() {
    if (!this.scenes.length || this.activeSceneIndex < 0) return undefined;

    const comingScenes = this.scenes.slice(
      this.activeSceneIndex,
      this.scenes.length
    );

    // Tack on all previous scenes in reverse order so that it downloads these
    // scenes when the comingscenes are done downloading
    const previousScenes = this.scenes
      .slice(0, this.activeSceneIndex)
      .reverse();

    const allScenes = [...comingScenes, ...previousScenes];

    const incompleted = allScenes.filter(
      ({ downloadStore }) => !downloadStore.completed
    );

    return incompleted.length ? incompleted[0] : null;
  }

  @computed
  public get allMapInfo() {
    const allMapInfo: MapInfo[] = [];
    for (const { mapInfo } of this.scenes) {
      allMapInfo.push(...mapInfo);
    }

    return allMapInfo;
  }

  @computed
  public get allVariables(): { [key: string]: string } {
    const variables = this.scenes.reduce((prev, current) => {
      const outputs: { [key: string]: string } = {};

      for (const [key, value] of Object.entries(current.outputData)) {
        outputs[key] = value;
      }
      return { ...prev, ...outputs };
    }, {});

    return {
      ...this.game?.divisionVariables,
      ...variables,
      ...this.game?.variables,
      points: `${this.game?.totalPoints || 0}`,
    };
  }
}
